import { AdvancedMethodsMyoReps } from '../enums';
import { DeclaredState, FitnessState, MyoRepsMode } from './user-state';
import { Exercise } from './exercise';
import { Plan_SubSlot, getMyoSetsFromSets } from './fitness-plan';

/** Whether the given exercise, as configured, will be scheduled as myo-reps
 *  (one activation set + mini-sets) instead of classic straight sets. */
export function exerciseUsesMyoReps(
  exercise: Exercise,
  numSets: number,
  declared: DeclaredState,
  fitnessState: FitnessState,
): boolean {
  // bail if it doesn't make sense at all
  if (numSets < 2 || !exercise.allowMyoReps) {
    return false;
  }

  const exerciseStats = fitnessState.exerciseStats[exercise._id];
  const preferredByUser = exerciseStats && exerciseStats.preferMyoReps;
  if (preferredByUser === MyoRepsMode.Prefer) {
    return true;
  } else if (preferredByUser === MyoRepsMode.Avoid) {
    return false;
  }

  switch (declared.advancedMethodsMyoReps) {
    case undefined: // for cases where this was not set yet
    case AdvancedMethodsMyoReps.Avoid:
      return false;
    case AdvancedMethodsMyoReps.Automatic:
      return numSets >= 5;
    case AdvancedMethodsMyoReps.Prefer:
      return numSets >= 2;
    default:
      return false;
  }
}

/**
 * Whether the exercise sitting in this plan sub-slot will be scheduled as myo-reps.
 *
 * It exists so that every caller asks the question the same way, because the argument is
 * easy to get wrong: `exerciseUsesMyoReps` wants the sub-slot's BASE count. For a myo-rep
 * exercise `defaultVolume_sets` is a combined classic-equivalent volume, which is the unit
 * that decision is defined in — an effective count that already includes a volume
 * adjustment is not.
 *
 * The `|| 1` matters too. Neither client nor shared has strictNullChecks, so an absent
 * `defaultVolume_sets` really can arrive here, and `exerciseUsesMyoReps` bails early on
 * `numSets < 2` — which `undefined < 2` does not satisfy, so without the floor an absent
 * count falls through to the per-user preference and answers true for anyone who prefers
 * myo-reps.
 */
export function subSlotUsesMyoReps(
  planSubSlot: Plan_SubSlot,
  exercise: Exercise,
  declaredState: DeclaredState,
  fitnessState: FitnessState,
): boolean {
  return exerciseUsesMyoReps(
    exercise, planSubSlot.defaultVolume_sets || 1, declaredState, fitnessState,
  );
}

/** Baseline top-set count for this subslot. Collapses the myo-rep
 *  combined-equivalent volume stored in defaultVolume_sets (e.g. 6 = [3, 3])
 *  into the actual number of top-level sets scheduled, so callers that
 *  compare against `exercise.sets.length` stay in the same unit. */
export function getMyoAwareNumSets(
  planSubSlot: Plan_SubSlot,
  exercise: Exercise,
  declaredState: DeclaredState,
  fitnessState: FitnessState,
): number {
  const useMyoReps = exerciseUsesMyoReps(
    exercise, planSubSlot.defaultVolume_sets, declaredState, fitnessState,
  );
  return useMyoReps
    ? getMyoSetsFromSets(planSubSlot.defaultVolume_sets).length
    : planSubSlot.defaultVolume_sets;
}
